import cn from "classnames"
import React from "react"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import ClientData from "@/common/client_data"
import Scrollable from "@/common/components/scrollable"
import env from "@/common/env"
import { sizeUnit } from "@/common/locales"
import Calc from "@/common/util/calculator"
import Icons from "@/images/signomatic/presets"
import alu_label from "@/images/signomatic/production_types/alu_label.png"
import { matchPreset } from "@/signomatic/app/sign_validator"
import SelectedIndicator from "@/signomatic/components/selected_indicator"
import Toolbox from "@/signomatic/components/toolbox"
import { type ToolboxStripItemProps } from "@/signomatic/components/toolbox_strip"
import { actions } from "@/signomatic/store/actions"
import { type State } from "@/signomatic/store/reducers"

const showReadMore = (presetName: string) => {
  return (
    !!ClientData.toolboxSnippet(presetName) ||
    ["label_anodised_aluminium", "label_lacquered_aluminium"].includes(presetName)
  )
}

const label = (preset: SignPreset) => {
  if (preset.label) {
    return preset.label
  }

  if (preset.badge_name) {
    return `badge.${preset.badge_name}`
  }

  return preset.description || ""
}

const description = (preset: SignPreset) => {
  if (preset.badge_name || preset.description) {
    return preset.description || ""
  }

  if (preset.description === null && !preset.material) {
    return ""
  }

  return `material_type.${preset.material}`
}

const size = (preset: SignPreset) => {
  const fixedSize = preset.settings.fixed_size
  if (!fixedSize) {
    return ""
  }

  const height = sizeUnit === "in" ? Calc.mmToIn(fixedSize.h) : fixedSize.h
  const width = sizeUnit === "in" ? Calc.mmToIn(fixedSize.w) : fixedSize.w
  return (
    <span className="paragraph-2xs">
      {width}
      {sizeUnit} x {height}
      {sizeUnit}
    </span>
  )
}

const presetId = (preset: SignPreset) => {
  let variant: string | null = null
  let shape: string | null = null
  let fixmethod: string | null = null

  if (preset.name) {
    return preset.name
  }

  if (preset.settings.shapes && preset.settings.shapes.length === 1 && preset.settings.shapes[0]) {
    shape = preset.settings.shapes[0].name
  }

  if (preset.settings.fixmethods) {
    if (preset.badge_name && preset.settings.fixmethods[0]) {
      // brass
      fixmethod = preset.settings.fixmethods[0].name
    } else if (preset.settings.fixmethods.length === 1 && preset.settings.fixmethods[0]) {
      // color badge
      fixmethod = preset.settings.fixmethods[0].name
    }
  }

  if (preset.material === "nickel" && preset.settings.color === "#c8c6cd") {
    variant = "blank"
  }

  if (preset.material === "brass" && preset.settings.color === "#d8c98d") {
    variant = "blank"
  }

  if (
    shape === "ellipse" &&
    preset.settings.fixed_size &&
    preset.settings.fixed_size.w === preset.settings.fixed_size.h
  ) {
    variant = "oval"
  }

  return [preset.badge_name, preset.material, shape, fixmethod, variant].filter(i => i).join("_")
}

interface PresetDetailsProps {
  name: string
}

const PresetDetails: React.FC<PresetDetailsProps> = ({ name }) => {
  const { t } = useTranslation()

  if (["label_anodised_aluminium", "label_lacquered_aluminium"].includes(name)) {
    const alu_variant = name == "label_anodised_aluminium" ? "laser_etching" : "lacquered"

    return (
      <div className="described-details">
        <h5>{t(`production_type.${alu_variant}_alu_description`)}</h5>
        <p className="paragraph-sm">{t(`production_type.${alu_variant}_alu_readmore`)}</p>
        {alu_variant === "laser_etching" && <img src={alu_label} alt="" className="mb-4" />}
      </div>
    )
  }

  return (
    <div className="described-details">
      <div className="description" dangerouslySetInnerHTML={{ __html: ClientData.toolboxSnippet(name) }} />
    </div>
  )
}

interface PresetProps {
  name: string
  preset: SignPreset
  active: boolean
  update: () => void
  showReadMore?: boolean
  onReadMore: (e: React.MouseEvent) => void
}

const Preset = ({ name, preset: { settings }, preset, active, update, showReadMore, onReadMore }: PresetProps) => {
  const { t } = useTranslation()
  const double = ["double_sided_alu", "double_sided_forex", "double_sided_ecoboard"].includes(name)

  return (
    <div id={presetId(preset)} className={cn("described", { active })} onClick={update}>
      <div className="contents">
        <div className="image">
          <img src={Icons[name]} />
        </div>
        <div className="details">
          <h5 className="mb-0">{t(label(preset))}</h5>
          <p className="paragraph-2xs mb-0">{t(description(preset))}</p>
          <p className="paragraph-2xs mb-0">
            {size(preset)}
            <span>
              {settings.fixmethods &&
                settings.fixmethods[0] &&
                settings.fixmethods[0].name !== "none" &&
                !preset.name.includes("stainless") &&
                preset.badge_name && <span> - {t(`fixmethod.${settings.fixmethods[0].name}`)}</span>}
            </span>
          </p>
          {showReadMore && (
            <div className="info-row">
              <p className="paragraph-xs mb-0">
                <a onClick={onReadMore}>{t("misc.readmore")} ...</a>
              </p>
              <SelectedIndicator active={active} />
            </div>
          )}
          {double && !showReadMore && (
            <div className="info-row right">
              <SelectedIndicator active={active} />
            </div>
          )}
        </div>
        {!double && !showReadMore && (
          <div className="info-row bottom">
            <SelectedIndicator active={active} />
          </div>
        )}
      </div>
    </div>
  )
}

interface PresetToolboxStateProps {
  data: SignData
  signState: SignState
  subToolbox: string
}

interface PresetToolboxDispatchProps {
  setSubToolbox: typeof actions.setSubToolbox
}

type PresetToolboxProps = PresetToolboxStateProps & PresetToolboxDispatchProps & ToolboxStripItemProps

const activePreset = (state: SignState, data: SignData, preset: SignPreset) => {
  if (preset.group_alias) {
    const group = data.preset_groups && data.preset_groups[preset.name]

    if (group) {
      return group.presets?.some(p => matchPreset(state, p)) || false
    }
  }

  return matchPreset(state, preset)
}

const PresetToolbox: React.FC<PresetToolboxProps> = ({
  sign,
  signState,
  presetGroupName,
  data,
  subToolbox,
  setSubToolbox,
}) => {
  const admin = !!(ClientData.admin && ClientData.admin.id)
  let presets: SignPreset[] | null | undefined = null
  let defaultPresets: SignPreset[] | null | undefined = null

  for (const key in data.preset_groups) {
    if (Object.prototype.hasOwnProperty.call(data.preset_groups, key)) {
      if (defaultPresets === null) {
        defaultPresets = data.preset_groups[key]?.presets
      }

      if (key === presetGroupName) {
        presets = data.preset_groups[key]?.presets
      }
    }
  }

  if (presets === null) {
    if (defaultPresets) {
      presets = defaultPresets
    } else {
      return null
    }
  }

  return (
    <Toolbox tKey="preset" labelKey={presetGroupName || ""}>
      <Scrollable scrollToActive scrollActiveToolboxDelay>
        {presets?.map(preset => {
          if (preset.settings.locale && !preset.settings.locale.includes(env.locale)) {
            return null
          }

          if (preset.settings.admin_only && !admin) {
            return
          }

          return (
            <Preset
              key={preset.name}
              name={preset.name}
              preset={preset}
              active={activePreset(signState, data, preset)}
              update={() => {
                sign.setPreset(preset.name)

                if (!showReadMore(preset.name)) {
                  setSubToolbox(null)
                } else if (subToolbox && subToolbox !== preset.name) {
                  setSubToolbox(preset.name)
                }
              }}
              showReadMore={showReadMore(preset.name)}
              onReadMore={e => {
                e.stopPropagation()
                e.preventDefault()
                setSubToolbox(preset.name)
              }}
            />
          )
        })}
      </Scrollable>
      {subToolbox && (
        <Scrollable>
          <PresetDetails name={subToolbox} />
        </Scrollable>
      )}
    </Toolbox>
  )
}

export default connect(
  ({ signState, currentSignData, subToolbox }: State) => ({
    signState,
    data: currentSignData,
    subToolbox,
  }),
  {
    setSubToolbox: actions.setSubToolbox,
  }
)(PresetToolbox)
